/**
 * Date conversion utilities between JavaScript Date and .NET DateTime ticks
 * .NET DateTime ticks: 100-nanosecond intervals since 0001-01-01 00:00:00
 * JavaScript Date: milliseconds since 1970-01-01 00:00:00
 */

/**
 * Converts JavaScript Date to .NET DateTime ticks
 * @param date - JavaScript Date object
 * @returns .NET ticks as bigint
 */
export function dateToTicks(date: Date): bigint {
  // .NET has 10000 ticks per millisecond
  const ticksPerMillisecond = 10000n;
  // Difference between .NET epoch (0001-01-01) and JavaScript epoch (1970-01-01)
  const epochDifference = 621355968000000000n;
  return BigInt(date.getTime()) * ticksPerMillisecond + epochDifference;
}

/**
 * Converts .NET DateTime ticks to JavaScript Date
 * @param ticks - .NET ticks (number or bigint)
 * @returns JavaScript Date object
 */
export function ticksToDate(ticks: number | bigint): Date {
  const ticksPerMillisecond = 10000n;
  const epochDifference = 621355968000000000n;
  const milliseconds = (BigInt(ticks) - epochDifference) / ticksPerMillisecond;
  return new Date(Number(milliseconds));
}

/**
 * Converts .NET DateTime ticks to JavaScript Date, accepting string for full precision.
 * Use when ticks may exceed Number.MAX_SAFE_INTEGER (2^53); API should send these as strings.
 * @param ticks - .NET ticks (number, string, bigint, or null/undefined)
 * @returns JavaScript Date object, or null when ticks is null/undefined
 */
export function ticksToDateSafe(ticks: number | string | bigint | null | undefined): Date | null {
  if (ticks == null || ticks === undefined) return null;
  const t = typeof ticks === 'string' ? BigInt(ticks) : ticks;
  return ticksToDate(t);
}

/**
 * Validates if ticks represent a future date
 * @param ticks - .NET ticks
 * @returns true if date is in the future
 */
export function isFutureTicks(ticks: number | bigint): boolean {
  if (ticks === 0n || ticks === 0) return false;
  const date = ticksToDate(ticks);
  return date.getTime() > Date.now();
}
